import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useOmniOfferRemoveScopeOfWorkMutation } from "../api/offer-api";

/**
 * OfferScopeOfWorkDeleteDialog removes the scope of work scopeOfWorkId
 * with its positions from the offer offerId after a confirmation.
 * description replaces the default description of the confirmation.
 */
export function OfferScopeOfWorkDeleteDialog({
  offerId,
  scopeOfWorkId,
  onDeleted,
  description = t(
    "Die Leistung wird mit allen Positionen gelöscht. Dies kann nicht rückgängig gemacht werden.",
  ),
  disabled,
}: {
  offerId: string;
  scopeOfWorkId?: string;
  onDeleted?: () => void;
  description?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [remove, { isLoading, error, reset }] =
    useOmniOfferRemoveScopeOfWorkMutation();

  const onConfirm = () => {
    if (isLoading || !scopeOfWorkId) {
      return;
    }
    remove({ id: offerId, scopeOfWorkId })
      .unwrap()
      .then(() => {
        toast.success(t("Leistung gelöscht"));
        reset();
        setOpen(false);
        onDeleted?.();
      })
      .catch(() => {
        // The error is shown by the RTKQueryErrorAlert.
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Trash2}
        label=""
        variant="destructive"
        disabled={disabled || !scopeOfWorkId}
        aria-label={t("Leistung löschen")}
      />
      <DialogContent>
        <CommonDialogHeader
          title={t("Leistung wirklich löschen?")}
          description={description}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
